import React from 'react';
import { Download } from 'lucide-react';

// Helper function to escape CSV values
const escapeCSV = (value) => {
  if (value == null || value === '') return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const downloadQuickBooksCSV = (invoiceData, pdfFile) => {
  let csv = '';
  
  // QuickBooks CSV Header
  csv += '*InvoiceNo,*Customer,CustomerEmail,BillingAddress,BillingCity,BillingState,BillingZip,BillingCountry,*InvoiceDate,*DueDate,Terms,Location,Memo,*Item(Product/Service),ItemDescription,ItemQuantity,ItemRate,*ItemAmount,Taxable,Service Date\n';
  
  const invoiceNo = escapeCSV(invoiceData['invoice-number']);
  
  // Use customer info (buyer perspective for QuickBooks)
  const customer = escapeCSV(invoiceData.customer.name || invoiceData.customer.number || 'Customer');
  const customerEmail = escapeCSV(invoiceData.customer.email || invoiceData['requester-email']);
  const billingAddress = escapeCSV(invoiceData.customer.address);
  const billingCity = escapeCSV(invoiceData.customer.city);
  const billingState = escapeCSV(invoiceData.customer.state);
  const billingZip = escapeCSV(invoiceData.customer.zip);
  const billingCountry = escapeCSV(invoiceData.customer.country);
  
  const invoiceDate = escapeCSV(invoiceData['invoice-date']?.split(' ')[0] || '');
  
  // Calculate default due date (30 days from invoice date) if due date is blank
  let dueDate = invoiceData['due-date']?.split(' ')[0] || '';
  if (!dueDate && invoiceData['invoice-date']) {
    const invoiceDateObj = new Date(invoiceData['invoice-date']);
    if (!isNaN(invoiceDateObj.getTime())) {
      const defaultDueDate = new Date(invoiceDateObj);
      defaultDueDate.setDate(defaultDueDate.getDate() + 30);
      dueDate = defaultDueDate.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: 'numeric' 
      });
    }
  }
  dueDate = escapeCSV(dueDate);
  
  const terms = escapeCSV(invoiceData['payment-terms']);
  const location = '';
  const memo = '';
   
  // Get charge amounts
  const shippingAmount = (invoiceData['shipping-amount'] && invoiceData['shipping-amount'] !== '') ? parseFloat(invoiceData['shipping-amount']) : 0;
  const handlingAmount = (invoiceData['handling-amount'] && invoiceData['handling-amount'] !== '') ? parseFloat(invoiceData['handling-amount']) : 0;
  const miscAmount = (invoiceData['misc-amount'] && invoiceData['misc-amount'] !== '') ? parseFloat(invoiceData['misc-amount']) : 0;
  const discountAmount = (invoiceData['discount-amount'] && invoiceData['discount-amount'] !== '') ? Math.abs(parseFloat(invoiceData['discount-amount'])) : 0;
  
  // Add regular line items
  invoiceData['invoice-lines'].forEach((line, index) => {
    const itemName = escapeCSV(line['item-type'] || 'Service');
    const itemDesc = escapeCSV(line.description || line['supplier-part-number'] || '');
    const itemQty = escapeCSV(line.quantity || '1');
    const itemRate = escapeCSV(line.price || '0');
    
    const lineTotal = (parseFloat(line.quantity || 0) * parseFloat(line.price || 0)).toFixed(2);
    const itemAmount = escapeCSV(line['custom-fields']['invoice-line-total'] || lineTotal);
  
    const serviceDate = '';
    
    // First line has all invoice header data
    if (index === 0) {
      csv += `${invoiceNo},${customer},${customerEmail},${billingAddress},${billingCity},${billingState},${billingZip},${billingCountry},${invoiceDate},${dueDate},${terms},${location},${memo},${itemName},${itemDesc},${itemQty},${itemRate},${itemAmount},N,${serviceDate}\n`;
    } else {
      // Subsequent lines - invoice number + empty customer fields + item data
      csv += `${invoiceNo},,,,,,,,,,,,,${itemName},${itemDesc},${itemQty},${itemRate},${itemAmount},N,${serviceDate}\n`;
    }
  });
  
  // Add shipping as a separate line item if exists
  if (shippingAmount > 0) {
    csv += `${invoiceNo},,,,,,,,,,,,,Shipping,Shipping,1,${shippingAmount.toFixed(2)},${shippingAmount.toFixed(2)},N,\n`;
  }
  
  // Add handling as a separate line item if exists
  if (handlingAmount > 0) {
    csv += `${invoiceNo},,,,,,,,,,,,,Handling,Handling,1,${handlingAmount.toFixed(2)},${handlingAmount.toFixed(2)},N,\n`;
  }
  
  // Add misc charges as a separate line item if exists
  if (miscAmount > 0) {
    csv += `${invoiceNo},,,,,,,,,,,,,Misc Charges,Misc Charges,1,${miscAmount.toFixed(2)},${miscAmount.toFixed(2)},N,\n`;
  }
  
  // Add discount as a separate line item if exists (negative amount)
  if (discountAmount > 0) {
    const negativeDiscount = -Math.abs(discountAmount);
    csv += `${invoiceNo},,,,,,,,,,,,,Discount,Discount,1,${negativeDiscount.toFixed(2)},${negativeDiscount.toFixed(2)},N,\n`;
  }
   
   // Add header-level taxes as separate line items if they exist
  if (invoiceData['tax-lines'] && invoiceData['tax-lines'].length > 0) {
    invoiceData['tax-lines'].forEach((tax) => {
      const taxAmt = parseFloat(tax.amount || 0).toFixed(2);
      const taxDesc = escapeCSV(tax.description || 'Tax');
      csv += `${invoiceNo},,,,,,,,,,,,,Service,${taxDesc},1,${taxAmt},${taxAmt},N,\n`;
    });
  }
  // Download
  const dataBlob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `quickbooks_invoice_${invoiceData['invoice-number']}.csv`;
  link.click();
};

export const QuickBooksExportButton = ({ invoiceData, pdfFile }) => {
  return (
    <button
      onClick={() => downloadQuickBooksCSV(invoiceData, pdfFile)}
      className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg transform hover:scale-105 transition-all"
    >
      <Download className="w-5 h-5" />
      Download QuickBooks CSV
    </button>
  );
};